import {
  CircularSpinnerß,
  cx,
  DialogContent,
  Dialogß,
} from '@otter-ai/den-components';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { useExperimentEnabled } from '@/experiment/experiments-client';
import { userWorkspaceQueryCreator } from '@/query/userWorkspaceQueryCreator';
import { workspaceQueryCreator } from '@/query/workspaceQueryCreator';
import { usePromiseValue } from '@/shared/hooks/usePromiseValue';
import { openDialog } from '@/shared/modals/openDialog';
import { generateUrlForEmbeddedConnectionQueryCreator } from '../query/generateUrlForEmbeddedConnectionQueryCreator';
import type { WorkatoApp } from '../type/workato';
import { useWorkatoIframeConnection } from './useWorkatoIframeConnection';

/**
 * Hook to open the Workato app settings modal.
 *
 * @example
 * ```tsx
 * function Example({workatoApp}) {
 *   const { openWorkatoAppSettingsModal } = useWorkatoAppSettingsModal();
 *   const onClick = async () => {
 *     console.log('Opening Workato App Settings Modal');
 *     await openWorkatoAppSettingsModal(workatoApp);
 *     console.log('Workato App Settings Modal closed');
 *   };
 *
 *   return (<button onClick={onClick}>Open Workato App Settings</button>);
 * }
 * ```
 */
export function useWorkatoAppSettingsModal() {
  const queryClient = useQueryClient();

  const openWorkatoAppSettingsModal = useCallback(
    async (app: WorkatoApp) => {
      const iframeUrlPromise = queryClient.fetchQuery(
        generateUrlForEmbeddedConnectionQueryCreator(app.id),
      );

      await openDialog(async ({ done }) => {
        return (
          <WorkatoAppSettingsModal
            done={done}
            app={app}
            iframeUrlPromise={iframeUrlPromise}
          />
        );
      });

      queryClient.invalidateQueries(
        generateUrlForEmbeddedConnectionQueryCreator(app.id),
      );
      queryClient.invalidateQueries(workspaceQueryCreator());
      queryClient.invalidateQueries(userWorkspaceQueryCreator());
    },
    [queryClient],
  );

  return { openWorkatoAppSettingsModal };
}

function WorkatoAppSettingsModal({
  done,
  app,
  iframeUrlPromise,
}: {
  done: () => void;
  app: WorkatoApp;
  iframeUrlPromise: Promise<string>;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const isDynamicDimensions = useExperimentEnabled(
    'workato-dynamic-dimensions',
  );
  const { iframeRef, iframeHeight } = useWorkatoIframeConnection({
    enabled: isDynamicDimensions,
  });
  const iframeUrl = usePromiseValue(iframeUrlPromise);
  const t = useTranslations('settings:integrations');

  return (
    <Dialogß
      data-testid="workato-app-settings-modal"
      onOpenChange={(open) => !open && done()}
      size="lg"
      title={app.name}
    >
      <DialogContent>
        {isLoading && (
          <div className="min-w-[750px] h-[min(640px,calc(100vh-64px))] flex items-center justify-center">
            <CircularSpinnerß
              size="xlg"
              data-testid="workato-app-settings-loading"
            />
          </div>
        )}
        {iframeUrl && (
          <iframe
            ref={iframeRef}
            onLoad={() => {
              setIsLoading(false);
            }}
            title={t('workato_authentication')}
            src={iframeUrl}
            height={isDynamicDimensions ? iframeHeight : 750}
            className={cx(
              'm-auto',
              isLoading && 'sr-only',
              isDynamicDimensions && 'min-w-[750px] w-full',
              !isDynamicDimensions && 'w-[750px]',
            )}
          />
        )}
        <TroubleShootingInfo app={app} />
      </DialogContent>
    </Dialogß>
  );
}

function TroubleShootingInfo({ app }: { app: WorkatoApp }) {
  return (
    <div className="text-right text-xs text-palette-gray-500 hover:text-subtle mt-2">
      ID: {app.id}, Provider: {app.provider}, Authorization Status:{' '}
      {app.authorization_status ?? 'None'}
    </div>
  );
}
